import useStorageSnapshot from '../../hooks/useStorageSnapshot';
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Divider,
  Button,
  useTheme,
  alpha,
  Chip,
  LinearProgress,
  Paper,
  styled,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  ShareOutlined as ShareOutlinedIcon,
  PeopleAlt as PeopleAltIcon,
  CloudUpload as CloudUploadIcon,
  AccessTime as AccessTimeIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  BarChart as BarChartIcon,
  Storage as StorageIcon,
  PermIdentity as PermIdentityIcon,
  Timeline as TimelineIcon,
  Add as AddIcon,
  SupervisorAccount as SupervisorAccountIcon,
  ChevronLeft as ChevronLeftIcon,
  Home as HomeIcon,
  PhotoLibrary as PhotoLibraryIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Movie as MovieIcon,
  MusicNote as MusicNoteIcon,
  Description as DescriptionIcon,
  Code as CodeIcon,
  Archive as ArchiveIcon,
  FilterAlt as FilterAltIcon,
  ManageAccounts as ManageAccountsIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import { toggleSidebar, setUploadDialogMinimized, openUploadDialog } from '../../store/slices/uiSlice';
import { selectTotalProgress, selectUploads } from '../../store/slices/uploadSlice';
import Logo from '../ui/Logo';
import analyticsService from '../../services/analyticsService';
import folderService from '../../services/folderService';
import { isStorageAdmin } from '../../utils/roleUtils';
import {
  resolveUploadFolderId,
  parseFilesViewFromSearch,
  parseRouteFolderId,
  isUploadDisabledForView
} from '../../utils/uploadContext';

// Constants
const TOPBAR_HEIGHT = 64;
const DRAWER_WIDTH = 240;

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  letterSpacing: '0.5px',
  padding: theme.spacing(1.5, 2, 0.5),
}));

const StorageStatusBar = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  margin: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
}));

const NavListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0.2, 0),
  padding: theme.spacing(1, 2),
  minHeight: 44,
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
    '& .MuiListItemText-primary': {
      fontWeight: 500,
      color: theme.palette.primary.main,
    },
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.action.hover, 0.8),
  },
}));

// Component that properly handles Link component from react-router-dom
const NavItem = ({ to, selected, onClick, children, ...props }: any) => (
  <NavListItemButton
    component={Link}
    to={to}
    selected={selected}
    onClick={onClick}
    {...props}
  >
    {children}
  </NavListItemButton>
);

const Sidebar: React.FC = () => {
  const { isSidebarOpen, isMobileView, uploadDialogMinimized } = useSelector((state: RootState) => state.ui);
  const { user } = useSelector((state: RootState) => state.auth);
  const { currentFolder, lastBrowsedFolderId } = useSelector((state: RootState) => state.folders);
  const uploadTotalProgress = useSelector(selectTotalProgress);
  const uploads = useSelector(selectUploads);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  // Collapsible section states
  const [openFileSection, setOpenFileSection] = useState(true);
  const [openLibrarySection, setOpenLibrarySection] = useState(false);
  const [openAnalyticsSection, setOpenAnalyticsSection] = useState(false);
  const [openAdminSection, setOpenAdminSection] = useState(false);
  const [rootFolderId, setRootFolderId] = useState<string | null>(null);

  useEffect(() => {
    folderService.getFolder('root').then((f) => setRootFolderId(f.id)).catch(() => {});
  }, []);

  const filesViewFromRoute = parseFilesViewFromSearch(location.search);
  const routeFolderId = parseRouteFolderId(location.pathname);

  const sidebarUploadFolderId = useMemo(
    () =>
      resolveUploadFolderId({
        routeFolderId,
        currentFolderId: currentFolder?.id,
        filesView: filesViewFromRoute,
        rootFolderId,
        lastBrowsedFolderId
      }),
    [
      routeFolderId,
      currentFolder?.id,
      filesViewFromRoute,
      rootFolderId,
      lastBrowsedFolderId
    ]
  );

  const sidebarUploadDisabled = isUploadDisabledForView(filesViewFromRoute);

  // Storage data state
  const [storageData, setStorageData] = useState<{
    used: number;
    total: number;
    percentage: number;
  } | null>(null);

  // Main navigation items
  const mainItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', badge: null },
    { text: 'Home', icon: <HomeIcon />, path: '/', badge: null },
  ];

  // File management items
  const fileItems = [
    { text: 'My Files', icon: <FolderIcon />, path: '/files', primary: true, badge: null },
    { text: 'Recent Files', icon: <AccessTimeIcon />, path: '/files?view=recent', badge: null },
    { text: 'Favorites', icon: <StarIcon />, path: '/files?view=favorites', badge: null },
    { text: 'Shared with Me', icon: <PeopleAltIcon />, path: '/files?view=shared-with-me', badge: null },
    { text: 'My Shares', icon: <ShareOutlinedIcon />, path: '/files?view=my-shared', badge: null },
    { text: 'Trash', icon: <DeleteIcon />, path: '/files?view=trash', badge: null },
  ];

  // Library items (file types)
  const libraryItems = [
    { text: 'Images', icon: <PhotoLibraryIcon />, path: '/files?view=images', badge: null },
    { text: 'Documents', icon: <DescriptionIcon />, path: '/files?view=documents', badge: null },
    { text: 'Videos', icon: <MovieIcon />, path: '/files?view=videos', badge: null },
    { text: 'Audio', icon: <MusicNoteIcon />, path: '/files?view=audio', badge: null },
    { text: 'Archives', icon: <ArchiveIcon />, path: '/files?view=archives', badge: null },
    { text: 'Code', icon: <CodeIcon />, path: '/files?view=code', badge: null },
  ];

  // Analytics items
  const analyticsItems = [
    { text: 'Usage Statistics', icon: <BarChartIcon />, path: '/analytics/usage', badge: null },
    { text: 'Activity', icon: <TimelineIcon />, path: '/activity', badge: null },
    { text: 'Storage Analysis', icon: <StorageIcon />, path: '/analytics/storage', badge: null },
  ];

  // Admin items
  const adminItems = [
    {
      text: 'User storage quotas',
      icon: <ManageAccountsIcon />,
      path: '/admin/user-storage',
      badge: null,
    },
  ];

  // Settings items
  const settingsItems = [
    { text: 'Account', icon: <PermIdentityIcon />, path: '/profile', badge: null },
  ];

  // Fetch storage data
  const {snapshot: storageSnapshot} = useStorageSnapshot();
  useEffect(() => {
    setStorageData(storageSnapshot ? {used: storageSnapshot.used / (1024 ** 3), total: storageSnapshot.assigned / (1024 ** 3), percentage: storageSnapshot.percentage} : null);
  }, [storageSnapshot]);

  // Toggle section expand/collapse
  const toggleSection = (section: string) => {
    switch (section) {
      case 'files':
        setOpenFileSection(!openFileSection);
        break;
      case 'library':
        setOpenLibrarySection(!openLibrarySection);
        break;
      case 'analytics':
        setOpenAnalyticsSection(!openAnalyticsSection);
        break;
      case 'admin':
        setOpenAdminSection(!openAdminSection);
        break;
      default:
        break;
    }
  };

  const handleDrawerClose = () => {
    dispatch(toggleSidebar());
  };

  // Check if a path is active
  const isActive = (path: string) => {
    // Handle query-based routes like /files?view=trash
    if (path.includes('?')) {
      const [targetPathname, targetQuery = ''] = path.split('?');
      if (location.pathname !== targetPathname) {
        return false;
      }

      const currentParams = new URLSearchParams(location.search);
      const targetParams = new URLSearchParams(targetQuery);
      let matches = true;
      targetParams.forEach((value, key) => {
        if (currentParams.get(key) !== value) {
          matches = false;
        }
      });
      return matches;
    }

    // Keep "My Files" active only for plain /files or folder navigation,
    // not when a specific view is selected via query params.
    if (path === '/files') {
      const isFilesPath = location.pathname === '/files' || location.pathname.startsWith('/files/');
      if (!isFilesPath) {
        return false;
      }
      return !new URLSearchParams(location.search).get('view');
    }

    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Render navigation items
  const renderNavItems = (items: any[]) => (
    <>
      {items.map((item) => (
        <ListItem key={item.text} disablePadding>
          <NavItem
            to={item.path}
            selected={isActive(item.path)}
            onClick={isMobileView ? handleDrawerClose : undefined}
          >
            <ListItemIcon sx={{ minWidth: 36, color: isActive(item.path) ? 'primary.main' : 'text.secondary' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text} 
              primaryTypographyProps={{ 
                fontSize: '0.875rem',
                fontWeight: isActive(item.path) ? 500 : 400,
              }}
            />
            {item.badge && (
              <Chip
                size="small"
                label={item.badge}
                color="primary"
                variant="filled"
                sx={{ 
                  height: 20, 
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            )}
          </NavItem>
        </ListItem>
      ))}
    </>
  );

  // Render collapsible section
  const renderSection = (title: string, items: any[], isOpen: boolean, sectionKey: string, icon: React.ReactNode) => (
    <>
      <ListItem disablePadding>
        <NavItem onClick={() => toggleSection(sectionKey)}>
          <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
            {icon}
          </ListItemIcon>
          <ListItemText 
            primary={title} 
            primaryTypographyProps={{ 
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          />
          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </NavItem>
      </ListItem>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pl: 2 }}>
          {renderNavItems(items)}
        </List>
      </Collapse>
    </>
  );

  // Render storage usage indicator
  const renderStorageUsage = () => {
    if (!storageData) {
      return null; // Don't render until data is loaded
    }

    const storageUsed = storageData.used;
    const storageTotal = storageData.total;
    const storagePercentage = storageData.percentage;
    
    return (
      <StorageStatusBar>
        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              Storage
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {storageUsed.toFixed(2)} GB of {storageTotal.toFixed(0)} GB used
            </Typography>
          </Box>
          <Tooltip title="Storage details">
            <IconButton size="small" onClick={() => navigate('/analytics/storage')}>
              <StorageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(storagePercentage, 100)}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: alpha(theme.palette.primary.main, 0.15),
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              backgroundColor: storagePercentage > 90 ? theme.palette.error.main : 
                             storagePercentage > 75 ? theme.palette.warning.main : 
                             theme.palette.primary.main,
            },
          }}
        />
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {storagePercentage.toFixed(1)}% used
          </Typography>
        </Box>
      </StorageStatusBar>
    );
  };

  // Sidebar content
  const sidebarContent = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header with logo */}
      <Box sx={{ 
        py: 1.5, 
        px: 2,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
        minWidth: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1, mr: 1 }}>
          <Logo size="medium" />
        </Box>
        
        {isMobileView && (
          <IconButton onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>
      
      {/* Upload button */}
      <Box sx={{ px: 2, py: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() =>
            !sidebarUploadDisabled &&
            dispatch(openUploadDialog({ folderId: sidebarUploadFolderId }))
          }
          disabled={sidebarUploadDisabled}
          sx={{
            py: 1,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          Upload
        </Button>
      </Box>
      
      {/* Scrollable navigation content */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        px: 1
      }}>
        {/* Main items */}
        <List disablePadding>
          {renderNavItems(mainItems)}
        </List>

        <Divider sx={{ my: 1.5, mx: 1 }} />

        {/* Files section */}
        <List disablePadding>
          {renderSection('Files', fileItems, openFileSection, 'files', <FolderIcon />)}
        </List>
        
        {/* Library section */}
        <List disablePadding sx={{ mt: 1 }}>
          {renderSection('Library', libraryItems, openLibrarySection, 'library', <InsertDriveFileIcon />)}
        </List>
        
        {/* Admin sections */}
        {isStorageAdmin(user?.role) && (
          <>
            <Divider sx={{ my: 1.5, mx: 1 }} />
            
            <List disablePadding>
              {renderSection('Analytics', analyticsItems, openAnalyticsSection, 'analytics', <BarChartIcon />)}
            </List>
            
            <List disablePadding sx={{ mt: 1 }}>
              {renderSection('Administration', adminItems, openAdminSection, 'admin', <SupervisorAccountIcon />)}
            </List>
          </>
        )}
        
        <Divider sx={{ my: 1.5, mx: 1 }} />
        
        <List disablePadding>
          <ListItem disablePadding>
            <NavItem to="/filters">
              <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                <FilterAltIcon />
              </ListItemIcon>
              <ListItemText primary="Smart Filters" primaryTypographyProps={{ fontSize: '0.875rem' }} />
            </NavItem>
          </ListItem>
        </List>
        
        {/* Storage usage */}
        {renderStorageUsage()}
      </Box>
      
      {/* Footer section */}
      <Box>
        <Divider />
        <List disablePadding sx={{ p: 1 }}>
          {renderNavItems(settingsItems)}
        </List>
        
        {uploadDialogMinimized && (
          <Box sx={{ px: 1, pb: 1 }}>
            <Paper
              elevation={2}
              onClick={() => dispatch(setUploadDialogMinimized(false))}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  dispatch(setUploadDialogMinimized(false));
                }
              }}
              sx={{
                p: 1.25,
                cursor: 'pointer',
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                },
              }}
            >
              <Typography variant="caption" fontWeight={700} display="block" color="primary" gutterBottom>
                Upload minimized
              </Typography>
              <LinearProgress
                variant="determinate"
                value={uploads.length > 0 ? Math.min(100, Math.max(0, uploadTotalProgress)) : 0}
                sx={{ height: 6, borderRadius: 1, mb: 0.75 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {uploads.length > 0
                  ? `${uploadTotalProgress}% · ${uploads.length} in queue — click to expand`
                  : 'Click to expand upload window'}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Copyright */}
        <Box sx={{ 
          px: 2, 
          py: 1.5, 
          textAlign: 'center',
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            © {new Date().getFullYear()} CYNAYD Drive
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            All rights reserved
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobileView ? 'temporary' : 'persistent'}
      open={isSidebarOpen}
      onClose={handleDrawerClose}
      ModalProps={{
        keepMounted: true, // Better open performance
      }}
      sx={{
        width: isSidebarOpen ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          top: TOPBAR_HEIGHT,
          height: `calc(100% - ${TOPBAR_HEIGHT}px)`,
          borderRight: `1px solid ${theme.palette.divider}`,
          boxShadow: isMobileView ? '0px 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
          zIndex: theme.zIndex.drawer,
          overflowX: 'hidden',
        },
      }}
    >
      {sidebarContent}
    </Drawer>
  );
};

export default Sidebar;